import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { setActiveTab } from '@store/slices/monitor.slice';
import { selectActiveTab, selectMonitorLayer } from '@store/selectors/monitor.selectors';
import { LayerList } from '../layers/LayerList';
import { SafetyEventsTab } from './SafetyEventsTab';
import styles from '../styles.module.css';

/** Left chevron for the "back" control on the full safety view. */
function BackArrow() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

type ILeftRailPanelProps = {
  /** The default card stack (live summary, filters, safety alerts). */
  cardStack: ReactNode;
};

/**
 * Owns the LeftRail's `activeTab` subscription and the scroll container.
 * Isolating `activeTab` here means a tab switch re-renders ONLY this
 * component — the (memoized) `cardStack` subtree and the sibling `MainView`
 * keep stable element references and are not touched.
 *
 * The safety view is mounted on demand: its events log is virtualized, so
 * mounting it is cheap (only on-screen rows render) and there is no need to
 * keep it alive while hidden.
 */
export function LeftRailPanel({ cardStack }: ILeftRailPanelProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeTab = useAppSelector(selectActiveTab);
  // The Activities / Issues layer swaps the card stack for that layer's list;
  // the full safety view (opened from a floor's alert badge) still wins.
  const layer = useAppSelector(selectMonitorLayer);
  const showLayerList = layer !== 'workforce' && activeTab !== 'safety';
  const contentRef = useRef<HTMLDivElement>(null);
  const workforceScrollRef = useRef(0);

  // The scroll container persists across the card-stack ↔ safety / layer
  // list swaps. "View all" and a layer list open at the top; coming back
  // restores wherever the user had scrolled the card stack.
  const showCardStack = activeTab !== 'safety' && !showLayerList;
  useLayoutEffect(() => {
    contentRef.current?.scrollTo({
      top: showCardStack ? workforceScrollRef.current : 0,
    });
  }, [showCardStack]);

  return (
    <ScrollArea
      className={styles.content}
      viewportRef={contentRef}
      viewportProps={{
        onScroll: (e) => {
          // Track the card-stack scroll position so "back" can restore it.
          if (showCardStack) {
            workforceScrollRef.current = e.currentTarget.scrollTop;
          }
        },
      }}
    >
      <div className={styles.cardStack} hidden={!showCardStack}>
        {cardStack}
      </div>
      {showLayerList && <LayerList />}
      {activeTab === 'safety' && (
        <div className={styles.safetyView}>
          <button
            type="button"
            className={styles.backRow}
            onClick={() => dispatch(setActiveTab('workforce'))}
          >
            <BackArrow />
            {t('monitor.safety.back')}
          </button>
          <SafetyEventsTab scrollRef={contentRef} />
        </div>
      )}
    </ScrollArea>
  );
}
